/** Operator detail page — OPERATOR_OPS #4.
 *
 * `/admin/operators/:userId` — replaces the old "Manage" modal with a full
 * page: header + stats + a progress (sessions-over-time) chart + status
 * donut, and the tabbed editor (Details / Schedule / Campaigns /
 * Permissions). Reached from the Operators section on /admin/members. */
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { SessionsLineChart } from "@/components/SessionsLineChart";
import { StatCard } from "@/components/StatCard";
import { StatusDonut } from "@/components/StatusDonut";
import { ApiPaths, api } from "@/lib/api";
import { OperatorEditorTabs, type OperatorSummary } from "@/modules/admin";

type OperatorRow = OperatorSummary & { is_active: boolean };

type StatsResp = {
  sessions: {
    total: number;
    this_week: number;
    done: number;
    upcoming: number;
    by_status: {
      closed: number;
      live: number;
      created: number;
      expired: number;
    };
  };
  campaign_count: number;
  schedule: {
    has_schedule: boolean;
    weekly_hours: number;
    days_worked: number;
    timezone: string;
    upcoming_time_off: number;
  };
  sessions_timeseries: { date: string; count: number }[];
};

export default function AdminOperatorDetailPage() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const uid = Number(userId);

  const operators = useQuery({
    queryKey: ["operators"],
    queryFn: async () =>
      (await api.get<OperatorRow[]>(ApiPaths.operators)).data,
  });
  const stats = useQuery({
    queryKey: ["operator-stats", uid],
    queryFn: async () =>
      (await api.get<StatsResp>(ApiPaths.operatorStats(uid))).data,
    enabled: Number.isFinite(uid),
  });

  const op = operators.data?.find((o) => o.user_id === uid);
  const name = op
    ? `${op.first_name} ${op.last_name}`.trim() || op.email
    : `#${uid}`;
  const s = stats.data;

  return (
    <AppLayout
      title={name}
      description={op?.email}
      headerAction={
        <BackLink fallback="/admin/members" className="btn-ghost text-sm">
          ← {t("admin.operators.backToMembers", "Members")}
        </BackLink>
      }
    >
      <div className="space-y-6">
        {/* Stat cards. */}
        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label={t("operatorDashboard.statUpcoming", "Upcoming")}
            value={s?.sessions.upcoming ?? "—"}
            tone="info"
          />
          <StatCard
            label={t("operatorDashboard.statThisWeek", "This week")}
            value={s?.sessions.this_week ?? "—"}
            tone="ok"
          />
          <StatCard
            label={t("operatorDashboard.statDone", "Completed")}
            value={s?.sessions.done ?? "—"}
            tone="rose"
          />
          <StatCard
            label={t("operatorDashboard.statCampaigns", "Campaigns")}
            value={s?.campaign_count ?? "—"}
            tone="warn"
          />
        </section>

        {/* Progress chart + status donut. */}
        {s && (
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
            <div className="card">
              <header className="mb-3 flex items-baseline justify-between">
                <h2 className="text-sm font-semibold text-nb-text">
                  {t("admin.operators.progressTitle", "Sessions (30 days)")}
                </h2>
              </header>
              <SessionsLineChart
                data={s.sessions_timeseries}
                label={t("admin.operators.progressTitle", "Sessions (30 days)")}
              />
            </div>
            <div className="card">
              <h2 className="mb-3 text-sm font-semibold text-nb-text">
                {t("dashboard.statusBreakdown", "Status breakdown")}
              </h2>
              <StatusDonut
                closed={s.sessions.by_status.closed}
                live={s.sessions.by_status.live}
                created={s.sessions.by_status.created}
                expired={s.sessions.by_status.expired}
                labels={{
                  closed: t("dashboard.byStatusClosed", "Closed"),
                  live: t("dashboard.byStatusLive", "Live"),
                  created: t("dashboard.byStatusCreated", "Created"),
                  expired: t("dashboard.byStatusExpired", "Expired"),
                }}
              />
            </div>
          </section>
        )}

        {/* Tabbed editor (Details / Schedule / Campaigns / Permissions). */}
        {op ? (
          <OperatorEditorTabs operator={op} />
        ) : operators.isLoading ? (
          <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
        ) : operators.isError ? (
          // Distinct from "not found" below — the roster fetch itself
          // failed, so we can't tell whether this operator exists.
          <p className="text-sm text-cvp-danger">
            {t("admin.operators.loadError", "Could not load operators.")}
          </p>
        ) : (
          <p className="text-sm text-nb-text-3">
            {t("admin.operators.notFound", "Operator not found.")}
          </p>
        )}
      </div>
    </AppLayout>
  );
}
